/* eslint-disable react-hooks/rules-of-hooks */
import {
  ElementRef,
  Fragment,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  AnswerAction,
  AnswerByField,
  FormFieldView,
  FormMode,
  FormStep,
  FormViewProps,
} from './Form.types'
import Styles from './Form.module.scss'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import OneText from '@onepercentio/one-ui/components/Text'
import Select from '@onepercentio/one-ui/components/Select'
import UncontrolledTransition from '@onepercentio/one-ui/components/UncontrolledTransition'
import Input from '@onepercentio/one-ui/components/Input'
import Button from '@onepercentio/one-ui/components/Button'
import Radio from '@onepercentio/one-ui/components/Radio'
import FileInput from '@onepercentio/one-ui/components/FileInput'
import CheckBox from '@onepercentio/one-ui/components/CheckBox'
import { TransitionAnimationTypes } from '@onepercentio/one-ui/components/Transition'
import AdaptiveContainer from '@onepercentio/one-ui/components/AdaptiveContainer'
import useModule from '@onepercentio/one-ui/hooks/utility/useModule'
import { AnchoredTooltipAlignment } from '@onepercentio/one-ui/components/AnchoredTooltip'
import CurrencyInput from '@onepercentio/one-ui/components/CurrencyInput'
import Card from 'components/Card/Card'
import IconRarum from 'components/IconRarum/IconRarum.view'
import Text from 'openspace/components/Text/Text'
import { FigmaTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import { i18n, t, tO } from 'translate/i18n'

import InputMask from 'react-input-mask'
import AddressFormLogic from 'features/Form/AddressForm/AddressForm.logic'
import {
  Address,
  AddressFormMode,
} from 'features/Form/AddressForm/AddressForm.types'
import { downloadLink } from 'utility/file'
import { getCVMDocumentLink } from 'core/modules/firebase/service'
import SectionLoader from 'openspace/components/SectionLoader/SectionLoader'
import Step from './Step'
import { StepsWrapper } from './Step/Step'
import { CVM_FORM_TEST_IDS } from './Form.e2e'
import { useUploadProgress } from './Form.hook'
import { UserAddress } from 'core/modules/firebase/service/user'

const Collapsable = ({
  children,
  hidden,
}: PropsWithChildren<{ hidden: boolean }>) => (
  <AdaptiveContainer direction='v'>
    {hidden ? (
      <Fragment key='hidden'></Fragment>
    ) : (
      <Fragment key='visible'>{children}</Fragment>
    )}
  </AdaptiveContainer>
)

export function FormFieldInput<Q extends FormFieldView>({
  config: c,
  onAnswer,
  value,
  error,
  mode,
  readOnlyView = 'card',
  ...props
}: {
  config: Q
  value: AnswerByField<Q>
  error?: string | string[]
  onAnswer: <T extends Q['type']>(
    questionType: T,
    questionId: string,
    answer:
      | AnswerAction<{
        type: T
      }>
      | undefined
  ) => void
  mode: FormMode
  readOnlyView?: 'card' | 'short'
  'data-testid'?: string
}) {
  const val = <T extends FormFieldView['type']>() =>
    value as unknown as
    | AnswerByField<{
      type: T
    }>
    | undefined
  const ans = <T extends FormFieldView['type']>(
    answer:
      | AnswerAction<{
        type: T
      }>
      | undefined
  ) =>
    answer as unknown as AnswerAction<{
      type: T
    }>
  const type = c.type
  const { Wrapper, textCls, titleCls } = useMemo(() => {
    switch (readOnlyView) {
      case 'card':
        return {
          Wrapper: Card,
          titleCls: FigmaTypo.H4,
          textCls: FigmaTypo.P16,
        } as const
      case 'short':
        return {
          Wrapper: ({ children }: PropsWithChildren) => children,
          titleCls: 'p12',
          textCls: 'p16',
        } as const
    }
  }, [readOnlyView])

  function toTitle() {
    return typeof c.title === 'string' ? (
      <OneText type='p12'>{c.title}</OneText>
    ) : (
      c.title
    )
  }
  function toPlaceholder() {
    return typeof c.title === 'string' ? c.title : c.placeholder
  }
  if (mode === FormMode.READ_ONLY) {
    switch (type) {
      case 'check':
        const checks = val<typeof type>()! || []
        return (
          <>
            <Wrapper>
              <OneText type={titleCls}>{c.title}</OneText>
              {checks.includes(true) ? (
                checks.map((checked, i) =>
                  checked ? (
                    <OneText type={textCls}>{c.options[i].label}</OneText>
                  ) : null
                )
              ) : (
                <OneText type={textCls}>-</OneText>
              )}
            </Wrapper>
            <Spacing size='16' />
          </>
        )
      case 'radio':
        const selectedRadio = val<typeof type>()! || ''
        return (
          <>
            <Wrapper>
              <OneText type={titleCls}>{c.title}</OneText>
              {selectedRadio ? (
                <OneText type={textCls}>
                  {c.options.find((el) => el.value === selectedRadio)!.label}
                </OneText>
              ) : (
                '-'
              )}
            </Wrapper>
            <Spacing size='16' />
          </>
        )

      case 'address':
        const [address] = val<typeof type>()! || [{} as UserAddress]
        return (
          <>
            <AddressFormLogic
              address={address}
              mode={AddressFormMode.READ_ONLY}
            />
            <Spacing size='16' />
          </>
        )
      case 'file':
        const file = val<typeof type>()!
        const firstExtensions = c.extensions
          .slice(0, c.extensions.length - 1)
          .map((a) => a.toUpperCase())
          .join(',')
        const lastExtension = c.extensions.slice(-1)[0]
        return (
          <>
            <FileInput
              states={{
                fileProvided: {
                  title: c.title,
                  description: tO(
                    `cvm88.form.disclaimers.${c.id as 'identification'}`
                  ),
                },
                waitingFile: {
                  title: c.title,
                  description: tO(
                    `cvm88.form.disclaimers.${c.id as 'identification'}`
                  ),
                },
              }}
              footer={`${firstExtensions} ${t(
                'generic.or'
              )} ${lastExtension.toUpperCase()}`}
              onFile={(file) => {
                onAnswer(type, c.id, ans<typeof type>(file))
              }}
              file={file === true ? ({} as File) : undefined}
              progress={file === true ? 100 : undefined}
              disabled={true}
            />
            <Spacing size='16' />
            {file === true && (
              <Button
                variant='transparent-link'
                color='primary'
                onClick={() => {
                  getCVMDocumentLink(c.id as 'document').then((link) => {
                    window.open(link, '_blank')
                  })
                }}>
                <IconRarum icon='download' size={24} />
                &nbsp;&nbsp;&nbsp;
                {tO('cvm88.form.sections.documents.actions.seeDocument')}
              </Button>
            )}
            <Spacing size='32' />
          </>
        )
      case 'select':
        const selectedOption = val<typeof type>()
        return (
          <>
            <Wrapper>
              <OneText type={titleCls}>{c.title}</OneText>
              <OneText type={textCls}>
                {tO(selectedOption as any) || '-'}
              </OneText>
            </Wrapper>
            <Spacing size='16' />
          </>
        )
      default:
        const answer = val<typeof type>()
        return (
          <>
            <Wrapper>
              <OneText type={titleCls}>{c.title}</OneText>
              <OneText type={textCls}>{answer || '-'}</OneText>
            </Wrapper>
            <Spacing size='16' />
          </>
        )
    }
  }
  switch (type) {
    case 'accept':
      const checks = val<typeof type>() || []
      return (
        <>
          <div className={Styles.acceptInput}>
            <div>
              <OneText type={FigmaTypo.H4}>{c.title}</OneText>
              <br />
              <Button
                variant='transparent-link'
                color='primary'
                onClick={() => {
                  switch (c.link.type) {
                    case 'file':
                      downloadLink(c.link.to, c.title as string)
                      break
                    case 'route':
                      window.open(c.link.to, '_blank')
                      break
                  }
                }}>
                <span>
                  <IconRarum icon='file' />
                </span>
                {c.link.type === 'file'
                  ? tO('cvm88.form.sections.investmentAmount.download', {
                    type: c.title,
                  })
                  : tO('cvm88.form.sections.investmentAmount.goTo', {
                    type: c.title,
                  })}
              </Button>
            </div>
            {error && (
              <div>
                <OneText type='caption' color='error'>
                  {error}
                </OneText>
                <br />
              </div>
            )}
            <div>
              {c.accept.map((a, i) => (
                <OneText type={FigmaTypo.P16}>
                  <CheckBox
                    label={
                      <span>
                        <>
                          {a.label}
                          {a.optional ? (
                            <>
                              <br />
                              <OneText type='caption'>
                                {tO('cvm88.form.optional')}
                              </OneText>
                            </>
                          ) : (
                            ''
                          )}
                        </>
                      </span>
                    }
                    checked={checks[i]}
                    onToggle={(checked) => {
                      const clone = [...checks]
                      clone[i] = checked
                      onAnswer(type, c.id, ans<typeof type>(clone))
                    }}
                    groupId={c.id}
                    value={a.label}
                  />
                </OneText>
              ))}
            </div>
          </div>
          <Spacing size='16' />
        </>
      )
    case 'file':
      const upload = val<typeof type>()
      const firstExtensions = c.extensions
        .slice(0, c.extensions.length - 1)
        .map((a) => a.toUpperCase())
        .join(',')
      const lastExtension = c.extensions.slice(-1)[0]
      const { progress } = useUploadProgress(upload as any)
      return (
        <>
          <FileInput
            states={{
              fileProvided: {
                title: c.title,
                description: tO(
                  `cvm88.form.disclaimers.${c.id as 'identification'}`
                ),
              },
              waitingFile: {
                title: c.title,
                description: tO(
                  `cvm88.form.disclaimers.${c.id as 'identification'}`
                ),
              },
            }}
            footer={`${firstExtensions} ${t(
              'generic.or'
            )} ${lastExtension.toUpperCase()}`}
            onFile={(file) => {
              onAnswer(type, c.id, ans<typeof type>(file))
            }}
            file={upload as unknown as File}
            progress={upload ? progress || 0 : undefined}
            data-testid={
              progress === 100 ? CVM_FORM_TEST_IDS.UPLOADED : undefined
            }
            accept={c.extensions.map((x) => `.${x}`).join(',')}
          />
          {error ? (
            <OneText type='caption' color='error'>
              {error}
            </OneText>
          ) : null}
          <Spacing size='32' />
        </>
      )
    case 'radio':
      const selected = val<typeof type>()
      return (
        <>
          <OneText type='p12'>{c.title}</OneText>
          {error && (
            <OneText type='caption' color='error'>
              {error}
            </OneText>
          )}
          <br />
          {c.options.map((el) => (
            <>
              <OneText type={FigmaTypo.P16}>
                <Radio
                  checked={selected === el.value}
                  label={el.label}
                  onToggle={() => onAnswer(type, c.id, el.value)}
                  groupId={c.id}
                  value={el.value}
                />
              </OneText>
              <br />
            </>
          ))}
          {c.footer && selected && (
            <>
              <UncontrolledTransition
                transitionType={TransitionAnimationTypes.COIN_FLIP}>
                {c.footer(selected as string)}
              </UncontrolledTransition>
              <br />
            </>
          )}
        </>
      )
    case 'check':
      const checkError =
        typeof error === 'string' ? c.options.map(() => error) : error
      const checkmarks = val<typeof type>() || []
      return (
        <>
          <OneText type='p12'>{c.title}</OneText>
          <br />
          {c.options.map((el, i) => (
            <>
              <OneText type={FigmaTypo.P16}>
                <CheckBox
                  checked={checkmarks[i]}
                  label={
                    <>
                      {el.label}
                      {checkError?.[i] ? (
                        <OneText type='caption' color='error'>
                          {checkError[i]}
                        </OneText>
                      ) : null}
                    </>
                  }
                  onToggle={(checked) => {
                    const clone = [...checkmarks]
                    clone[i] = checked
                    onAnswer(type, c.id, clone)
                  }}
                  groupId={c.id}
                  value={el.value}
                />
              </OneText>
              <br />
            </>
          ))}
        </>
      )
    case 'rawcheck':
      const rawCheckmarks = val<typeof type>() || []
      return (
        <>
          {error && (
            <>
              <OneText type='caption' color='error'>
                {error}
              </OneText>
              <br />
            </>
          )}
          {c.options.map((el, i) => (
            <>
              <Card className={Styles.card}>
                {el.header && (
                  <>
                    <Text code={el.header.title} type={FigmaTypo.H4} />
                    <Text code={el.header.description} type={FigmaTypo.P14} />
                    <Spacing size='32' />
                  </>
                )}
                <OneText type={FigmaTypo.P16}>
                  <CheckBox
                    checked={rawCheckmarks[i]}
                    label={el.label}
                    onToggle={(checked) => {
                      const clone = [...rawCheckmarks]
                      clone[i] = checked
                      onAnswer(type, c.id, clone)
                    }}
                    groupId={c.id}
                    value={el.value}
                  />
                </OneText>
              </Card>
              <br />
            </>
          ))}
        </>
      )
    case 'address':
      return (
        <AddressFormLogic
          address={val<typeof type>()?.[0] || ({} as Address)}
          onAddress={(address, isComplete, errors) => {
            onAnswer(
              type,
              c.id,
              ans<typeof type>([
                address,
                isComplete,
                !isComplete ? t('generic.fieldRequired') : false,
              ])
            )
          }}
          mode={AddressFormMode.WRITE}
          showErrors={!!error}
        />
      )
    case 'currency':
      return (
        <>
          {toTitle()}
          <CurrencyInput
            currency='BRL'
            locale={i18n.language}
            value={val()}
            onChange={(val) => {
              onAnswer(type, c.id, ans<typeof type>(val))
            }}
          />
        </>
      )
    case 'select':
      return (
        <>
          <OneText type='p12'>{c.title}</OneText>
          <Select
            data-testid={CVM_FORM_TEST_IDS.SELECT(c.id)}
            alignTo={AnchoredTooltipAlignment.LEFT}
            items={c.options}
            selected={val<typeof type>()!}
            onClick={(i) => {
              onAnswer(type, c.id, i.value)
            }}
            filter={c.filter}
            error={error as string}
          />
        </>
      )
    case 'date':
      return (
        <>
          <OneText type='p12'>{c.title}</OneText>
          <InputMask
            mask={'99/99/9999'}
            value={val<typeof type>()}
            onChange={({ target: { value } }) => {
              const answer = ans<typeof type>(value)
              onAnswer(type, c.id, answer)
            }}>
            {
              ((inputProps: any) => (
                <Input
                  {...inputProps}
                  placeholder={c.title}
                  error={error}
                  data-testid={CVM_FORM_TEST_IDS.QUESTION(c.id as any)}
                />
              )) as unknown as ReactNode
            }
          </InputMask>
        </>
      )
    case 'phone':
      const [_phone] = val<typeof type>() || ['', false]
      const phoneFormatter = useModule(() =>
        import('google-libphonenumber').then((mod) => {
          return mod.PhoneNumberUtil.getInstance()
        })
      )
      const ddis = useMemo(() => {
        return Object.values(require('utility/ddis.json')) as {
          pais: string
          ddi: number
        }[]
      }, [])
      const [selectedDDI, setDDI] = useState('55')

      const phone = useMemo(() => {
        return _phone.slice(selectedDDI.length)
      }, [_phone])
      const {
        phoneFormat,
        valid,
        error: phoneError,
      } = useMemo(() => {
        const country = phoneFormatter?.getRegionCodeForCountryCode(Number(selectedDDI))
        if (!phoneFormatter)
          return {
            phoneFormat: phone,
            valid: false,
            error: '',
          }
        try {
          const phoneInstance = phoneFormatter.parseAndKeepRawInput(
            phone,
            country
          )
          const isPhoneValid = phoneFormatter.isValidNumber(phoneInstance)
          return {
            phoneFormat: phoneFormatter.format(phoneInstance, 2),
            valid: isPhoneValid,
            error: isPhoneValid ? '' : t('generic.invalidPhone'),
          }
        } catch (e) {
          return {
            phoneFormat: phone,
            valid: false,
            error: t('generic.invalidPhone'),
          }
        }
      }, [phone, phoneFormatter])

      useEffect(() => {
        onAnswer(type, c.id, ans<typeof type>([`${selectedDDI}${phoneFormat}`, valid, phoneError]))
      }, [phoneFormat, valid])

      return (
        <>
          <OneText type='p12'>{c.title}</OneText>
          <div className={Styles.phoneContainer}>
            <Select
              items={ddis.map((a) => ({
                label: `(+${a.ddi}) ${a.pais}`,
                value: String(a.ddi),
              }))}
              onClick={({ value }) => {
                setDDI(value)
                onAnswer(
                  type,
                  c.id,
                  ans<typeof type>([
                    `${value}${phone}`,
                    false,
                    phoneError,
                  ])
                )
              }}
              selected={selectedDDI}
            />
            <Input
              placeholder={c.title as string}
              value={phoneFormat}
              onChange={({ target: { value } }) => {
                onAnswer(
                  type,
                  c.id,
                  ans<typeof type>([
                    `${selectedDDI}${value}`,
                    false,
                    phoneError,
                  ])
                )
              }}
              error={error as string}
            />
          </div>
        </>
      )
    case 'text':
    case 'number':
      const filter =
        type === 'number'
          ? (val: string) => val.replace(/[^0-9]+/g, '')
          : (val: string) => val
      return (
        <>
          {toTitle()}
          {type === 'text' && c.mask ? (
            <InputMask
              mask={c.mask}
              value={val<typeof type>()}
              onChange={({ target: { value } }) => {
                const answer = ans<typeof type>(value)
                onAnswer(type, c.id, answer)
              }}>
              {
                ((inputProps: any) => (
                  <Input
                    {...inputProps}
                    placeholder={c.title}
                    error={error}
                    multiline={c.multiline}
                    data-testid={CVM_FORM_TEST_IDS.QUESTION(c.id as any)}
                  />
                )) as unknown as ReactNode
              }
            </InputMask>
          ) : (
            <Input
              placeholder={toPlaceholder()}
              value={val<typeof type>()}
              error={error as string}
              onChange={({ target: { value } }) => {
                onAnswer(type, c.id, filter(value))
              }}
              data-testid={
                props['data-testid'] || CVM_FORM_TEST_IDS.QUESTION(c.id as any)
              }
              multiline={c.multiline}
            />
          )}
          {c.footer && c.footer(val() || '')}
        </>
      )
    default:
      throw new Error('Not implemented yet')
  }
}

/**
 * The form for the user to upload CVM88 information
 **/
export default function FormView({
  headings,
  sections,
  step,
  onBack,
  next,
  answers,
  onAnswer,
  errors,
}: FormViewProps) {
  if (step === undefined) return <SectionLoader />

  const [showErrors, setShowErrors] = useState(false)
  useEffect(() => {
    setShowErrors(false)
  }, [step])

  const errorsToShow = useMemo(() => {
    const requiredText = t('generic.fieldRequired')
    if (showErrors) return errors
    else
      return Object.entries(errors)
        .filter(([_, errorText]) => errorText !== requiredText)
        .reduce(
          (map, [k, v]) => ({
            ...map,
            [k]: v,
          }),
          {}
        )
  }, [showErrors, errors])

  return (
    <>
      <Text type={FigmaTypo.H2} code='cvm88.form.title' />
      <Spacing size='16' />
      <StepsWrapper currStep={step} steps={headings.length}>
        {headings.map((props, idx) => (
          <Step {...props} stepNumber={idx + 1} key={String(idx)} />
        ))}
      </StepsWrapper>
      <Spacing size='32' />
      <FormQuestions
        sections={sections}
        onAnswer={onAnswer}
        answers={answers}
        errors={errorsToShow}
        mode={FormMode.WRITE}
        step={step}
      />
      <div className={Styles.actions}>
        <Button key='action' variant='outline' color='primary' onClick={onBack}>
          {t('generic.goBack')}
        </Button>
        <Button
          variant='filled'
          color='primary'
          onClick={() => {
            if (next.disabled) setShowErrors(true)
            else next.action()
          }}>
          {next.label}
        </Button>
      </div>
    </>
  )
}

export function FormQuestions({
  sections,
  onAnswer,
  errors,
  answers,
  mode,
  step,
  hideNonRequiredFields,
}: Pick<FormViewProps, 'sections' | 'onAnswer' | 'errors' | 'answers'> & {
  mode: FormMode
  step: FormStep | number
  hideNonRequiredFields?: boolean
}) {
  const key = useMemo(
    () => Math.random().toFixed(10),
    [sections.map((s) => s.title).join('')]
  )
  const unRef = useRef<ElementRef<typeof UncontrolledTransition>>(null)
  const prevStep = useRef<number>()
  useInsertionEffect(() => {
    if (prevStep.current)
      if (prevStep.current > step) unRef.current!.setOrientation('backward')
      else unRef.current!.setOrientation('forward')
    return () => {
      prevStep.current = step
    }
  }, [step])

  return (
    <UncontrolledTransition ref={unRef} className={Styles.transitioner}>
      <Fragment key={key}>
        {sections.map(({ title, questions, description }) => (
          <section
            key={title}
            className={`${Styles.formSection} ${Styles[FormMode[mode]] || ''}`}>
            <OneText type={FigmaTypo.H3}>{title}</OneText>
            {description && (
              <>
                <br />
                <OneText type={FigmaTypo.P16}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: description,
                    }}
                  />
                </OneText>
              </>
            )}
            <Spacing size='32' />
            <div>
              {questions.map((q) => {
                const Wrapper = 'hidden' in q ? Collapsable : Fragment
                const props = 'hidden' in q ? { hidden: q.hidden } : ({} as any)
                if (hideNonRequiredFields && q.optional) return null
                return (
                  <Wrapper key={q.id} {...props}>
                    <FormFieldInput
                      config={q}
                      onAnswer={onAnswer as any}
                      value={answers[q.id] as any}
                      error={errors[q.id]}
                      mode={q.readonly ? FormMode.READ_ONLY : mode}
                      readOnlyView={q.readonly ? 'short' : 'card'}
                    />
                  </Wrapper>
                )
              })}
            </div>
          </section>
        ))}
      </Fragment>
    </UncontrolledTransition>
  )
}
